import React from 'react';
import { parseISO, format } from 'date-fns';
import { MONTH_DAY_YEAR_FORMAT } from '@dealroadshow/uikit-date-time/dateFormats';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import formatNumber from '@framework/data-helpers/formatters/formatNumber';
import {
  UNDERWRITER_NAME,
  RATING_AGENCY_NAME,
} from '@/creditflowresearch/ui/components/TopCompanies/constants';

import styles from './styles.module.scss';

const getNameCell = ({ rowData: { name } }) => (
  <Tooltip
    content={ name }
    containerClassName={ styles.name }
  >
    { name }
  </Tooltip>
);

const getDealCount = ({ rowData: { dealCount } }) => (
  <span className={ styles.dealCountWrp }>
    <Tooltip content="# of Issuances">
      <span className={ styles.dealCount }>{ dealCount }</span>
    </Tooltip>
  </span>
);

const getIssueDate = ({ rowData: { latestDealDate } }) => {
  const parsedDate = parseISO(new Date(latestDealDate).toISOString());
  return (
    <span className={ styles.date }>
      <Tooltip content="Last Issuance Date">
        <span>{ format(parsedDate, MONTH_DAY_YEAR_FORMAT) }</span>
      </Tooltip>
    </span>
  );
};

const getTotalSize = ({ rowData: { totalSize, size } }) => (
  <span className={ styles.totalSize }>
    <Tooltip content="Total Size">
      <span>{ `USD ${ formatNumber(totalSize || size) }m` }</span>
    </Tooltip>
  </span>
);

const getIssuerTicker = ({ rowData: { issuerTicker } }) => (
  <span className={ styles.issuerTicker }>
    <Tooltip content="Issuer Ticker">
      <span>{ issuerTicker }</span>
    </Tooltip>
  </span>
);

// common columns
const ABBREVIATION_CELL = {
  name: 'abbreviation',
  title: '',
  sortable: false,
  width: 45,
  maxWidth: 45,
  minWidth: 45,
};

const TOTAL_SIZE_CELL = {
  name: 'totalSize',
  title: '',
  sortable: false,
  cellCallback: getTotalSize,
  width: 130,
  maxWidth: 130,
  minWidth: 130,
};

const NAME_CELL = {
  name: 'name',
  title: '',
  className: styles.nameWrp,
  sortable: false,
  cellCallback: getNameCell,
};

const DEAL_COUNT_CELL = {
  name: 'dealCount',
  title: '',
  sortable: false,
  cellCallback: getDealCount,
  width: 45,
  maxWidth: 45,
  minWidth: 45,
};

const LATEST_DEAL_DATE = {
  name: 'latestDealDate',
  title: '',
  sortable: false,
  cellCallback: getIssueDate,
  width: 85,
  maxWidth: 85,
  minWidth: 85,
};

const ISSUER_TICKER_CELL = {
  name: 'issuerTicker',
  title: '',
  sortable: false,
  cellCallback: getIssuerTicker,
  width: 65,
  maxWidth: 65,
  minWidth: 65,
};

const getIssuersColumns = ({ withTicker = false, withSize = false, withDate = false }) => [
  withTicker && ISSUER_TICKER_CELL,
  NAME_CELL,
  withSize && TOTAL_SIZE_CELL,
  DEAL_COUNT_CELL,
  withDate && LATEST_DEAL_DATE,
].filter(Boolean);

const getUnderwritersAndAgenciesColumns = ({ withSize = false }) => [
  ABBREVIATION_CELL,
  NAME_CELL,
  withSize && TOTAL_SIZE_CELL,
  DEAL_COUNT_CELL,
].filter(Boolean);

export const topCompaniesTablesCommonProps = {
  containerClassName: styles.container,
  tableClassName: styles.table,
  emptyMessageClassName: styles.emptyMessage,
  rowClassName: styles.row,
  showColumnsHeaders: false,
  isItemsPerPageVisible: false,
  withSpinner: false,
};

interface IGetTopCompanyColumns {
  showVolume: boolean,
  companyViewId?: string | string[],
  companyRole: string,
}

export const getTopCompanyColumns = ({
  showVolume,
  companyViewId,
  companyRole,
}: IGetTopCompanyColumns) => {
  switch (companyRole) {
    case RATING_AGENCY_NAME:
    case UNDERWRITER_NAME:
      return getUnderwritersAndAgenciesColumns({ withSize: showVolume });
    default:
      return getIssuersColumns({
        withTicker: !!companyViewId,
        withSize: showVolume,
        withDate: !!companyViewId && !showVolume,
      });
  }
};
